import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

const pageHeaderVariants = cva(
  'flex flex-col gap-4 md:flex-row md:items-start md:justify-between',
)

const pageHeaderTitleVariants = cva('', {
  variants: {
    variant: {
      lobby: 'text-2xl md:text-4xl',
      utility: 'text-[2rem] md:text-[36px] leading-[1.2] tracking-tight',
    },
  },
  defaultVariants: {
    variant: 'lobby',
  },
})

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof pageHeaderTitleVariants> {
  leading?: React.ReactNode
  /**
   * Optically align the leading element flush with the title text.
   *
   * Ghost / text-only back buttons carry internal padding that pushes their
   * visible content inward.  Setting `leadingInset` applies a small negative
   * left margin (`-ml-3`) so the arrow / label visually aligns with the
   * heading below — matching the hover-state appearance at rest.
   *
   * @default true — covers the common "ghost back-button" pattern.
   * Set to `false` for badges, icon-only circles, or other leading elements
   * that already align flush.
   */
  leadingInset?: boolean
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ leading, leadingInset = true, title, description, action, variant = 'lobby', className, ...props }, ref) => (
    <header ref={ref} className={cn(pageHeaderVariants(), className)} data-page-header-variant={variant ?? 'lobby'} {...props}>
      <div className="space-y-2">
        {leading ? <div className={cn(leadingInset && '-ml-3')}>{leading}</div> : null}
        {typeof title === 'string' ? (
          <Heading level={1} className={pageHeaderTitleVariants({ variant })}>
            {title}
          </Heading>
        ) : title}
        {description
          ? typeof description === 'string'
            ? <Text size="sm" color="muted">{description}</Text>
            : description
          : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
  ),
)
PageHeader.displayName = 'PageHeader'
